import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { checkPlatformTenantSlug, createPlatformTenant } from '@/api/platformApi'
import { getPlatformTenantsPath } from '@/config/platform'
import { TenantPortalLinks } from '@/components/Platform/TenantPortalLinks'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Platform.module.css'

const WIZARD_STEPS = [
  {
    icon: '◎',
    title: 'Tenant na base',
    text: 'Cria uma linha em tenants com slug único e nome amigável.',
  },
  {
    icon: '◇',
    title: 'Conteúdo inicial',
    text: 'Prepara chaves de conteúdo por defeito para o novo negócio.',
  },
  {
    icon: '◉',
    title: 'Primeiro admin',
    text: 'Credenciais para o painel /admin — só desta conta.',
  },
] as const

export function PlatformNovaTenantPage() {
  const navigate = useNavigate()
  const [slug, setSlug] = useState('')
  const [nome, setNome] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminSenha, setAdminSenha] = useState('')
  const [adminNome, setAdminNome] = useState('Administrador')
  const [erro, setErro] = useState('')
  const [okMsg, setOkMsg] = useState('')
  const [createdSlug, setCreatedSlug] = useState<string | null>(null)
  const [createdNome, setCreatedNome] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [slugCheck, setSlugCheck] = useState<'idle' | 'checking' | 'ok' | 'bad'>('idle')
  const [slugHint, setSlugHint] = useState('')
  const { notifySaved, notifyError } = useSaveFeedback()

  useEffect(() => {
    const raw = slug.trim()
    if (raw.length < 2) {
      setSlugCheck('idle')
      setSlugHint('')
      return
    }
    let cancelled = false
    setSlugCheck('checking')
    const t = window.setTimeout(() => {
      checkPlatformTenantSlug(raw)
        .then((r) => {
          if (cancelled) return
          if (!r.available) {
            setSlugCheck('bad')
            setSlugHint(
              r.reason === 'reserved'
                ? 'Slug reservado pelo sistema.'
                : r.reason === 'invalid'
                  ? 'Formato inválido (minúsculas, números, hífens).'
                  : 'Este slug já está em uso.'
            )
          } else {
            setSlugCheck('ok')
            setSlugHint(`Disponível: /e/${r.slug}`)
          }
        })
        .catch(() => {
          if (!cancelled) {
            setSlugCheck('idle')
            setSlugHint('')
          }
        })
    }, 400)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [slug])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')
    if (slugCheck === 'bad') {
      setErro('Escolha outro slug antes de continuar.')
      return
    }
    setOkMsg('')
    setLoading(true)
    try {
      const res = await createPlatformTenant({
        slug: slug.trim(),
        nome: nome.trim() || undefined,
        adminEmail: adminEmail.trim(),
        adminSenha,
        adminNome: adminNome.trim() || undefined,
      })
      setCreatedSlug(res.slug)
      setCreatedNome(nome.trim() || res.nome || res.slug)
      const msg = `Conta «${res.nome || res.slug}» criada. Envie o link da app ao cliente.`
      setOkMsg(msg)
      notifySaved(msg)
      setSlug('')
      setNome('')
      setAdminEmail('')
      setAdminSenha('')
    } catch (err) {
      notifyError(err, 'Erro ao criar.')
      setErro(err instanceof Error ? err.message : 'Erro ao criar.')
    } finally {
      setLoading(false)
    }
  }

  const slugPreview = slug.trim() || 'seu-slug'
  const step1Done = slug.trim().length >= 2 && slugCheck === 'ok'
  const step2Ready = Boolean(adminEmail.trim() && adminSenha.length >= 6)

  return (
    <div className={styles.platPage}>
      <div className={styles.createHero}>
        <div className={styles.createHeroGlow} aria-hidden />
        <div className={styles.createHeroInner}>
          <span className={styles.createHeroEyebrow}>Provisionamento</span>
          <h1 className={styles.createHeroTitle}>Nova conta de cliente</h1>
          <p className={styles.createHeroSub}>
            Identifique o negócio na plataforma e crie o primeiro administrador com acesso ao painel{' '}
            <code className={styles.createHeroCode}>/admin</code>.
          </p>
        </div>
        <Link className={styles.createHeroBack} to={getPlatformTenantsPath()}>
          ← Contas
        </Link>
      </div>

      {erro && (
        <p className={styles.erro} role="alert">
          {erro}
        </p>
      )}

      {okMsg && createdSlug && (
        <section className={styles.createSuccess} aria-live="polite">
          <div className={styles.createSuccessIcon} aria-hidden>
            ✓
          </div>
          <div className={styles.createSuccessBody}>
            <h2 className={styles.createSuccessTitle}>Conta criada com sucesso</h2>
            <p className={styles.createSuccessMsg}>{okMsg}</p>
          </div>
          <TenantPortalLinks slug={createdSlug} nome={createdNome ?? undefined} />
          <div className={styles.createSuccessActions}>
            <button
              type="button"
              className={styles.btnOutline}
              onClick={() => {
                setCreatedSlug(null)
                setCreatedNome(null)
                setOkMsg('')
              }}
            >
              Criar outra conta
            </button>
            <Link className={styles.btnGradient} to={getPlatformTenantsPath()}>
              Ver lista de contas
            </Link>
          </div>
        </section>
      )}

      {!createdSlug && (
        <div className={styles.createLayout}>
          <form className={styles.createForm} onSubmit={handleSubmit}>
            <nav className={styles.createStepper} aria-label="Passos do formulário">
              <div className={`${styles.createStep} ${step1Done ? styles.createStepDone : styles.createStepActive}`}>
                <span className={styles.createStepNum}>1</span>
                <span className={styles.createStepLabel}>Negócio</span>
              </div>
              <span className={styles.createStepLine} aria-hidden />
              <div className={`${styles.createStep} ${step2Ready ? styles.createStepDone : step1Done ? styles.createStepActive : ''}`}>
                <span className={styles.createStepNum}>2</span>
                <span className={styles.createStepLabel}>Administrador</span>
              </div>
            </nav>

            <section className={styles.createSection}>
              <header className={styles.createSectionHead}>
                <span className={styles.createSectionIcon} aria-hidden>
                  ▣
                </span>
                <div>
                  <h2 className={styles.createSectionTitle}>Identificação do negócio</h2>
                  <p className={styles.createSectionSub}>Slug único e nome público na plataforma</p>
                </div>
              </header>

              <div className={styles.createUrlPreview}>
                <span className={styles.createUrlPrefix}>Link público</span>
                <code className={styles.createUrlValue}>
                  /e/<span className={styles.createUrlSlug}>{slugPreview}</span>
                </code>
                {slugCheck === 'checking' && <span className={styles.createUrlStatus}>A verificar…</span>}
                {slugCheck === 'ok' && <span className={styles.createUrlOk}>Disponível</span>}
                {slugCheck === 'bad' && <span className={styles.createUrlBad}>Indisponível</span>}
              </div>

              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="nt-slug">
                  Slug da conta
                </label>
                <input
                  id="nt-slug"
                  className={`${styles.platInput} ${slugCheck === 'ok' ? styles.platInputOk : ''} ${slugCheck === 'bad' ? styles.platInputBad : ''}`}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="ex.: salao-bela-vista"
                  required
                  autoComplete="off"
                />
                <p className={styles.platHint}>
                  Minúsculas, números e hífens. Identificador único em toda a plataforma.
                </p>
                {slugCheck === 'bad' && slugHint && (
                  <p className={styles.slugBad} role="alert">
                    {slugHint}
                  </p>
                )}
              </div>

              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="nt-nome">
                  Nome público do negócio
                </label>
                <input
                  id="nt-nome"
                  className={styles.platInput}
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex.: Salão Bela Vista"
                />
                <p className={styles.platHint}>Nome comercial — aparece em listagens e relatórios internos.</p>
              </div>
            </section>

            <section className={styles.createSection}>
              <header className={styles.createSectionHead}>
                <span className={styles.createSectionIcon} aria-hidden>
                  ◉
                </span>
                <div>
                  <h2 className={styles.createSectionTitle}>Primeiro administrador</h2>
                  <p className={styles.createSectionSub}>
                    Acesso ao painel do negócio — não confundir com este painel da plataforma
                  </p>
                </div>
              </header>

              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="nt-email">
                  E-mail do administrador
                </label>
                <input
                  id="nt-email"
                  type="email"
                  className={styles.platInput}
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="gestor@cliente.pt"
                  required
                  autoComplete="off"
                />
              </div>

              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="nt-senha">
                  Senha inicial
                </label>
                <input
                  id="nt-senha"
                  type="password"
                  className={styles.platInput}
                  value={adminSenha}
                  onChange={(e) => setAdminSenha(e.target.value)}
                  minLength={6}
                  required
                  autoComplete="new-password"
                />
                <p className={styles.platHint}>Mínimo 6 caracteres. Recomende alteração no primeiro login.</p>
              </div>

              <div className={styles.platField}>
                <label className={styles.platLabel} htmlFor="nt-anome">
                  Nome no painel <span className={styles.createOptional}>(opcional)</span>
                </label>
                <input
                  id="nt-anome"
                  className={styles.platInput}
                  value={adminNome}
                  onChange={(e) => setAdminNome(e.target.value)}
                  placeholder="Ex.: Maria — recepção"
                />
              </div>
            </section>

            <footer className={styles.createFormFooter}>
              <button
                type="submit"
                className={styles.btnGradient}
                disabled={loading || slugCheck === 'bad' || slugCheck === 'checking'}
              >
                {loading ? 'A criar conta…' : 'Criar conta do cliente'}
              </button>
              <button type="button" className={styles.linkGhost} onClick={() => navigate(getPlatformTenantsPath())}>
                Cancelar
              </button>
            </footer>
          </form>

          <aside className={styles.createAside} aria-label="Informação">
            <div className={styles.createAsideCard}>
              <h3 className={styles.createAsideTitle}>O que acontece ao guardar</h3>
              <ol className={styles.createAsideSteps}>
                {WIZARD_STEPS.map((step, i) => (
                  <li key={step.title} className={styles.createAsideStep}>
                    <span className={styles.createAsideStepIcon} aria-hidden>
                      {step.icon}
                    </span>
                    <div>
                      <strong className={styles.createAsideStepTitle}>
                        {i + 1}. {step.title}
                      </strong>
                      <p className={styles.createAsideStepText}>{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className={styles.createAsideTip}>
              <span className={styles.createAsideTipIcon} aria-hidden>
                ◈
              </span>
              <p>
                Após criar, copie o <strong>link da app</strong> e envie ao cliente por WhatsApp ou e-mail. Eles
                entram em <code>/e/slug</code> e usam o e-mail e senha definidos aqui.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
